import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Trash2, Plus, ShieldCheck, Loader2 } from 'lucide-react';

/**
 * Admin-only page: manage the Google-sign-in email allowlist.
 * Only listed emails can complete the "Sign in with Google" flow.
 */
export const AdminAllowlist = () => {
  const { API, user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState('');
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true); setError('');
    try {
      const r = await axios.get(`${API}/auth/allowlist`);
      setRows(r.data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load allowlist.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);   // eslint-disable-line

  const add = async (e) => {
    e.preventDefault();
    const email = newEmail.trim().toLowerCase();
    if (!email) return;
    setAdding(true); setError('');
    try {
      await axios.post(`${API}/auth/allowlist`, { email });
      setNewEmail('');
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to add.');
    } finally {
      setAdding(false);
    }
  };

  const remove = async (email) => {
    if (!window.confirm(`Remove ${email} from the Google allowlist?`)) return;
    setError('');
    try {
      await axios.delete(`${API}/auth/allowlist/${encodeURIComponent(email)}`);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to remove.');
    }
  };

  if (user && user.role !== 'operator' && user.role !== 'admin') {
    return (
      <div className="p-8 text-center text-text-muted" data-testid="admin-allowlist-forbidden">
        Admin access required.
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-6" data-testid="admin-allowlist">
      <div className="flex items-center gap-3 mb-6">
        <ShieldCheck className="h-5 w-5 text-sage" />
        <h1 className="text-2xl font-serif">Google Sign-in Allowlist</h1>
      </div>
      <p className="text-sm text-text-muted mb-2">
        Only emails on this list can sign in via Google. Anyone else attempting the flow will see an
        "access denied" screen and never enter the app.
      </p>
      <div className="bg-primary/5 border border-primary/20 rounded-md p-3 text-xs text-text-secondary mb-6" data-testid="allowlist-vs-access-note">
        This is the <strong>global Google login gate</strong> — it decides who may sign in with Google at all.
        To decide <strong>which client's data</strong> a signed-in user may see, use the <em>Access</em> section on that client's record (Clients tab → open a client).
      </div>

      <form onSubmit={add} className="flex gap-2 mb-4" data-testid="admin-allowlist-add-form">
        <input
          type="email"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          placeholder="new-user@example.com"
          className="flex-1 border border-border rounded-md px-3 py-2 text-sm bg-card"
          required
          data-testid="admin-allowlist-input"
        />
        <button
          type="submit"
          disabled={adding}
          className="inline-flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-md text-sm font-semibold disabled:opacity-50"
          data-testid="admin-allowlist-add-btn"
        >
          {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          Add
        </button>
      </form>

      {error && (
        <div className="text-sm text-ochre mb-3" data-testid="admin-allowlist-error">{error}</div>
      )}

      <div className="border border-border rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-background">
            <tr>
              <th className="text-left px-4 py-2 font-semibold">Email</th>
              <th className="text-left px-4 py-2 font-semibold">Added by</th>
              <th className="text-left px-4 py-2 font-semibold">Added at</th>
              <th className="w-16"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="4" className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan="4" className="text-center py-8 text-text-muted">No emails on the allowlist yet.</td></tr>
            ) : (
              rows.map((r) => (
                <tr key={r.email} className="border-t border-border" data-testid={`admin-allowlist-row-${r.email}`}>
                  <td className="px-4 py-2">{r.email}</td>
                  <td className="px-4 py-2 text-text-muted">{r.added_by || '—'}</td>
                  <td className="px-4 py-2 text-text-muted text-xs">
                    {r.added_at ? new Date(r.added_at).toLocaleString() : '—'}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button
                      onClick={() => remove(r.email)}
                      className="text-text-muted hover:text-ochre"
                      title="Remove"
                      data-testid={`admin-allowlist-remove-${r.email}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminAllowlist;
